import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Activity, BookOpen, Flag, Sparkles, Square, Table2, Timer, X } from 'lucide-react';
import useStreamingAutoScroll from '../../hooks/useStreamingAutoScroll';
import DetailedSessionAnalysisContent from './DetailedSessionAnalysisContent';
import DeepAnalysisChat from './DeepAnalysisChat';
import ReflectionPrompt from './ReflectionPrompt';
import SessionRecapContent from './SessionRecapContent';
import styles from './SessionRecapDialog.module.css';

const FOCUSABLE = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function statusCopy(report, kind) {
  if (!report) return kind === 'detailed' ? 'Deep Analysis ready to generate' : 'Standing by';
  if (report.status === 'queued') return kind === 'detailed'
    ? 'Waiting for Quick Debrief' : 'Waiting for Deep Analysis';
  if (report.status === 'building') return kind === 'detailed'
    ? 'Building deep evidence' : 'Building telemetry briefing';
  if (report.status === 'streaming') return kind === 'detailed'
    ? 'Generating Deep Analysis' : 'Generating Debrief';
  if (report.status === 'correcting') return 'Checking Deep Analysis';
  if (report.status === 'done') return kind === 'detailed' ? 'Deep Analysis ready' : 'Debrief ready';
  return 'Debrief unavailable';
}

const isGenerating = report => ['building', 'streaming', 'correcting'].includes(report?.status);

function generationCopy(report, kind) {
  if (report?.status === 'building') return kind === 'detailed'
    ? 'Assembling telemetry + history'
    : 'Assembling session evidence';
  if (report?.status === 'correcting') return 'Cross-checking measured claims';
  return kind === 'detailed' ? 'Synthesizing corner vectors' : 'Writing your driver debrief';
}

export default function SessionRecapDialog({
  recapController,
  trackName,
  sessionLabel,
  lapTraces,
  selectedLaps,
  corners,
  sectors,
  onClose,
  onOpenDeepDive,
}) {
  const dialogRef = useRef(null);
  const scrollPositionsRef = useRef({ standard: 0, detailed: 0 });
  const [activeTab, setActiveTab] = useState('standard');
  // Asked once per dialog, before the first deep run. Skipping is
  // remembered so the question never becomes a repeated toll.
  const [askingReflection, setAskingReflection] = useState(false);
  const [reflectionAsked, setReflectionAsked] = useState(false);
  const {
    recap, deepAnalysis, deepChat, busy, deepBusy, chatBusy,
    generate, generateDeep, setReflection, traceCoaching, stop, stopDeep, askDeep, stopDeepChat,
    clearDeepChat, deepQuestionSuggestions,
  } = recapController;
  const activeReport = activeTab === 'detailed' ? deepAnalysis : recap;
  const activeBusy = chatBusy || (activeTab === 'detailed' ? deepBusy : busy);
  const activeGenerate = activeTab === 'detailed' ? generateDeep : generate;
  const activeStop = chatBusy ? stopDeepChat
    : activeTab === 'detailed' ? stopDeep : stop;
  const deepWorkspaceReady = activeTab === 'detailed'
    && deepAnalysis?.status === 'done' && deepAnalysis?.chatReady;
  const generationKind = isGenerating(deepAnalysis) ? 'detailed'
    : isGenerating(recap) ? 'standard' : null;
  const generationReport = generationKind === 'detailed' ? deepAnalysis
    : generationKind === 'standard' ? recap : null;
  const quickScroll = useStreamingAutoScroll({
    status: recap?.status,
    content: recap?.text,
  });
  const deepScroll = useStreamingAutoScroll({
    status: deepAnalysis?.status,
    content: deepAnalysis?.text,
  });
  const {
    containerRef: bodyRef,
    onScroll: handleBodyScroll,
    onWheel: handleBodyWheel,
    onTouchStart: handleBodyTouchStart,
    onTouchMove: handleBodyTouchMove,
    onKeyDown: handleBodyKeyDown,
  } = activeTab === 'detailed' ? deepScroll : quickScroll;

  // Streaming follows the growing report by scrolling the outer tab panel.
  // Once the completed desktop workspace mounts, the report and chat become
  // independent inner scrollers. Clear the old outer scroll offset before
  // paint or it clips the top of both columns (including the chat controls).
  useLayoutEffect(() => {
    if (!deepWorkspaceReady || !bodyRef.current) return;
    bodyRef.current.scrollTop = 0;
    scrollPositionsRef.current.detailed = 0;
  }, [bodyRef, deepWorkspaceReady]);

  const startDeep = reflection => {
    setAskingReflection(false);
    if (setReflection) setReflection(reflection);
    generateDeep();
  };

  const selectTab = nextTab => {
    if (nextTab === activeTab) return;
    scrollPositionsRef.current[activeTab] = bodyRef.current?.scrollTop || 0;
    setActiveTab(nextTab);
    if (nextTab !== 'detailed' || deepAnalysis || deepBusy) return;
    // Ask for context once, before the first deep run. Answering is optional;
    // skipping goes straight to the analysis and is not asked again.
    // Only ask when the new surfaces are enabled; otherwise go straight
    // to the analysis as before.
    if (traceCoaching && !reflectionAsked) {
      setReflectionAsked(true);
      setAskingReflection(true);
      return;
    }
    generateDeep();
  };

  const handleTabKeyDown = event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextTab = ['ArrowRight', 'End'].includes(event.key) ? 'detailed' : 'standard';
    selectTab(nextTab);
    requestAnimationFrame(() => document.getElementById(`session-recap-tab-${nextTab}`)?.focus());
  };

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = scrollPositionsRef.current[activeTab] || 0;
    });
    return () => cancelAnimationFrame(frame);
  }, [activeTab, bodyRef]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const dialog = dialogRef.current;
    dialog?.querySelector('button')?.focus();

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = [...dialog.querySelectorAll(FOCUSABLE)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.();
    };
  }, [onClose]);

  const historyMeta = activeReport?.meta || recap?.meta;
  const historyCopy = historyMeta
    ? (historyMeta.hasHistory
      ? `${historyMeta.nDays} day${historyMeta.nDays === 1 ? '' : 's'} · ${historyMeta.nSessions} sessions here`
      : 'First logged day at this track')
    : 'Telemetry + driver history';

  return (
    <div className={styles.backdrop} role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section
        ref={dialogRef}
        className={`${styles.dialog} ${deepWorkspaceReady ? styles.dialogDeep : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-recap-title"
        aria-describedby="session-recap-context"
      >
        <div className={styles.gridTexture} aria-hidden="true" />
        <header className={styles.header}>
          <div className={styles.identity}>
            <span className={styles.mark} aria-hidden="true">
              <Sparkles size={21} />
              <i />
            </span>
            <div>
              <span className={styles.eyebrow}>Track Day Analysis</span>
              <h2 id="session-recap-title">AI Session Debrief</h2>
              <p id="session-recap-context">
                {[trackName, sessionLabel].filter(Boolean).join(' · ') || 'Current session'}
              </p>
            </div>
          </div>
          {generationReport && (
            <div
              className={styles.generationTelemetry}
              data-stage={generationReport.status}
              role="status"
              aria-live="polite"
            >
              <div className={styles.telemetryViz} aria-hidden="true">
                <svg viewBox="0 0 132 42" preserveAspectRatio="none">
                  <path
                    className={styles.traceGhost}
                    d="M0 31 L13 31 L22 20 L34 25 L45 8 L56 15 L67 12 L77 28 L89 22 L99 31 L111 13 L122 18 L132 10"
                  />
                  <path
                    className={styles.traceLive}
                    d="M0 31 L13 31 L22 20 L34 25 L45 8 L56 15 L67 12 L77 28 L89 22 L99 31 L111 13 L122 18 L132 10"
                  />
                </svg>
                <span className={styles.telemetrySweep} />
                <span className={styles.dataBars}>
                  <i /><i /><i /><i /><i />
                </span>
              </div>
              <div className={styles.generationCopy}>
                <strong>
                  {generationKind === 'detailed' ? 'Deep Analysis Active' : 'AI Debrief Active'}
                  <span><i /><i /><i /></span>
                </strong>
                <span>{generationCopy(generationReport, generationKind)}</span>
              </div>
            </div>
          )}
          <button className={styles.close} onClick={onClose} aria-label="Close AI Debrief">
            <X size={19} />
          </button>
        </header>

        <div className={styles.tabs} role="tablist" aria-label="Session debrief depth">
          <button
            id="session-recap-tab-standard"
            type="button"
            role="tab"
            aria-selected={activeTab === 'standard'}
            tabIndex={activeTab === 'standard' ? 0 : -1}
            aria-controls="session-recap-panel-standard"
            className={activeTab === 'standard' ? styles.activeTab : ''}
            onClick={() => selectTab('standard')}
            onKeyDown={handleTabKeyDown}
          >
            <Timer size={15} /> Quick Debrief
            {busy && <i aria-label="Generating" />}
          </button>
          <button
            id="session-recap-tab-detailed"
            type="button"
            role="tab"
            aria-selected={activeTab === 'detailed'}
            tabIndex={activeTab === 'detailed' ? 0 : -1}
            aria-controls="session-recap-panel-detailed"
            className={activeTab === 'detailed' ? styles.activeTab : ''}
            onClick={() => selectTab('detailed')}
            onKeyDown={handleTabKeyDown}
          >
            <BookOpen size={15} /> Deep Analysis
            {deepBusy && <i aria-label="Generating" />}
            {deepAnalysis?.status === 'done' && <em>Ready</em>}
          </button>
        </div>

        <div className={styles.statusRail}>
          <span className={`${styles.liveDot} ${activeBusy ? styles.live : ''}`} aria-hidden="true" />
          <strong aria-live="polite">
            {chatBusy ? (deepChat.status === 'checking'
              ? 'Checking follow-up answer' : 'Answering follow-up')
              : statusCopy(activeReport, activeTab)}
          </strong>
          <span className={styles.statusDivider} aria-hidden="true" />
          <span>{historyCopy}</span>
          <Activity size={14} aria-hidden="true" />
        </div>

        <div
          className={`${styles.body} ${deepWorkspaceReady ? styles.deepBody : ''}`}
          ref={bodyRef}
          tabIndex={0}
          onScroll={handleBodyScroll}
          onWheel={handleBodyWheel}
          onTouchStart={handleBodyTouchStart}
          onTouchMove={handleBodyTouchMove}
          onKeyDown={handleBodyKeyDown}
          id={`session-recap-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`session-recap-tab-${activeTab}`}
        >
          {activeTab === 'standard' ? (
            <SessionRecapContent
              recap={recap}
              variant="dialog"
              trackMapData={{
                lapTraces,
                selectedLaps,
                corners,
                sectors,
                gaps: recap?.meta?.gaps,
                sectorGaps: recap?.meta?.sectorGaps,
              }}
            />
          ) : (
            askingReflection ? (
              <ReflectionPrompt
                onSubmit={startDeep}
                onSkip={() => startDeep(null)}
              />
            ) : deepWorkspaceReady ? (
              <div className={styles.deepWorkspace}>
                <div className={styles.deepReportColumn}>
                  <DetailedSessionAnalysisContent
                    analysis={deepAnalysis}
                    trackMapData={{ lapTraces, selectedLaps, corners, sectors }}
                  />
                </div>
                <DeepAnalysisChat
                  chat={deepChat}
                  busy={chatBusy}
                  suggestions={deepQuestionSuggestions}
                  onAsk={askDeep}
                  onStop={stopDeepChat}
                  onClear={clearDeepChat}
                  onInspectCorner={onOpenDeepDive}
                />
              </div>
            ) : (
              <DetailedSessionAnalysisContent
                analysis={deepAnalysis}
                trackMapData={{ lapTraces, selectedLaps, corners, sectors }}
              />
            )
          )}
        </div>

        <footer className={styles.footer}>
          <button type="button" className={styles.deepDiveCopy} onClick={() => onOpenDeepDive()}>
            <span className={styles.flagIcon} aria-hidden="true"><Flag size={17} /></span>
            <div>
              <strong>Want the metrics behind the advice?</strong>
              <span>Inspect braking, minimum speed, throttle, and per-lap traces.</span>
            </div>
          </button>
          <div className={styles.actions}>
            {activeBusy ? (
              <button className={styles.secondary} onClick={activeStop}>
                <Square size={13} fill="currentColor" /> Stop
              </button>
            ) : activeReport ? (
              <button className={styles.secondary} onClick={activeGenerate}>
                <Sparkles size={14} /> Regenerate
              </button>
            ) : (
              <button className={styles.secondary} onClick={activeGenerate}>
                <Sparkles size={14} /> {activeTab === 'detailed' ? 'Generate Deep Analysis' : 'Generate'}
              </button>
            )}
            <button className={styles.secondary} onClick={onClose}>
              <X size={14} /> Close
            </button>
            <button className={styles.deepDive} onClick={() => onOpenDeepDive()}>
              <Table2 size={16} /> Open Corner Deep Dive
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
